/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '../../atoms/Text/Text';
import { ProgressBar } from '../ProgressBar';
import { Table, TableSize } from '../Table/Table';
import { PhotoCarousel } from '../PhotoCarousel/PhotoCarousel';
import { Modal, ModalBody, ModalHeader } from '../Modal';
import { PDFViewer } from '../PDFViewer/PDFViewer';
import { FilePreviewThumb } from './FilePreviewThumb';
import './FileHandlerStyles.scss';
const PDF_PREVIEW_MODAL_CLASS = 'qmb-ui-modal--document';
// ─── Module-level action renderer factory ────────────────────────────────────
// Keyed by a stable per-instance ID so multiple FileHandlers coexist.
const actionRendererRegistry = new Map();
// ─── Module-level preview click registry ────────────────────────────────────
const previewClickRegistry = new Map();
function buildActionRenderer(instanceId) {
    // eslint-disable-next-line react/display-name
    return function FileHandlerActionCell({ item, dataKey: _dataKey, index: _index }) {
        const entry = actionRendererRegistry.get(instanceId);
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return React.createElement(React.Fragment, null, entry ? entry.fn(item) : null);
    };
}
let fileHandlerIdCounter = 0;
function nextFileHandlerId() {
    fileHandlerIdCounter += 1;
    return fileHandlerIdCounter;
}
// ─── Helpers ────────────────────────────────────────────────────────────────
/**
 * Returns `true` when the file's MIME type is consistent with `accept`.
 * Handles MIME wildcards (e.g. `image/*`) and bare extensions (e.g. `.pdf`).
 * A missing `accept` string always passes.
 */
function fileMatchesAccept(file, accept) {
    if (!accept)
        return true;
    const parts = accept.split(',').map((s) => s.trim().toLowerCase());
    const fileMime = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    return parts.some((part) => {
        if (part.startsWith('.')) {
            return fileName.endsWith(part);
        }
        if (part.endsWith('/*')) {
            return fileMime.startsWith(part.slice(0, -1));
        }
        return fileMime === part;
    });
}
function partitionFiles(files, accept, maxSize) {
    const accepted = [];
    const sizeRejected = [];
    const typeRejected = [];
    files.forEach((file) => {
        if (maxSize !== undefined && file.size > maxSize) {
            sizeRejected.push(file);
        }
        else if (!fileMatchesAccept(file, accept)) {
            typeRejected.push(file);
        }
        else {
            accepted.push(file);
        }
    });
    return { accepted, sizeRejected, typeRejected };
}
function statusIconClass(status) {
    if (status === 'done')
        return 'fa-check';
    if (status === 'error')
        return 'fa-triangle-exclamation';
    if (status === 'uploading')
        return 'fa-spinner fa-spin';
    // Uploaded but not yet server-confirmed — distinct from both 'error' (failed) and 'done' (confirmed success).
    if (status === 'unconfirmed')
        return 'fa-clock';
    return 'fa-file';
}
const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.tiff', '.tif'];
const NON_IMAGE_EXTS = ['.txt', '.zip', '.csv', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.json', '.xml'];
function urlPath(url) {
    return url.toLowerCase().split('?')[0].split('#')[0];
}
/**
 * Infers the preview kind for a FileHandlerItem.
 * PDF is checked first (covers `.pdf` names, Report/PDF labels, PDF-looking URLs).
 * Photo covers 'Photo' label, image extensions, or a previewUrl on a non-PDF / non-other item.
 * Blob object URLs are never treated as PDF just because they exist.
 */
export function inferKind(item) {
    var _a, _b;
    const nameLower = item.name.toLowerCase();
    const path = urlPath((_b = (_a = item.downloadUrl) !== null && _a !== void 0 ? _a : item.previewUrl) !== null && _b !== void 0 ? _b : '');
    const isPdfUrl = path.endsWith('.pdf') && !path.startsWith('blob:');
    if (nameLower.endsWith('.pdf') || item.typeLabel === 'Report' || item.typeLabel === 'PDF' || isPdfUrl) {
        return 'pdf';
    }
    if (NON_IMAGE_EXTS.some((ext) => nameLower.endsWith(ext)))
        return 'other';
    if (item.typeLabel === 'Photo' || IMAGE_EXTS.some((ext) => nameLower.endsWith(ext) || path.endsWith(ext))) {
        return 'photo';
    }
    if (item.previewUrl)
        return 'photo';
    return 'other';
}
// ─── Table cell renderers (module-level — stable references) ─────────────────
function buildPreviewCellRenderer(instanceId) {
    // eslint-disable-next-line react/display-name
    return function FileHandlerPreviewCell({ item, dataKey: _dataKey, index: _index }) {
        const entry = previewClickRegistry.get(instanceId);
        const kind = inferKind(item);
        const clickable = kind === 'photo' || kind === 'pdf' || (kind === 'other' && Boolean(item.downloadUrl));
        const thumb = (React.createElement(FilePreviewThumb, { kind: kind, previewUrl: item.previewUrl, downloadUrl: item.downloadUrl, statusIconClass: item.status && item.status !== 'done' ? statusIconClass(item.status) : undefined }));
        if (clickable && entry) {
            return (React.createElement("button", { type: "button", className: "qmb-ui-file-handler__preview-cell qmb-ui-file-handler__preview-cell--clickable", "aria-label": `Preview ${item.name}`, onClick: (event) => {
                    event.stopPropagation();
                    entry.fn(item);
                } }, thumb));
        }
        return (React.createElement("div", { className: "qmb-ui-file-handler__preview-cell", "aria-hidden": "true" }, thumb));
    };
}
function NameCellRenderer({ item }) {
    var _a;
    return (React.createElement("div", { className: "qmb-ui-file-handler__name-cell" },
        React.createElement("span", { className: "qmb-ui-file-handler__row-name", title: item.name }, item.name),
        item.meta && React.createElement("span", { className: "qmb-ui-file-handler__row-secondary" }, item.meta),
        item.status === 'uploading' && (React.createElement(ProgressBar, { value: (_a = item.progress) !== null && _a !== void 0 ? _a : 0, showLabel: true, className: "qmb-ui-file-handler__row-progress" }))));
}
// ─── Main component ─────────────────────────────────────────────────────────
/**
 * FileHandler — general-purpose file upload dropzone + staged-file list.
 *
 * Visual pattern matches PhotoGallery (dashed dropzone, row list with preview)
 * but is **not** image-only. It performs client-side validation only; all server
 * upload logic belongs in the parent. For image-gallery contexts (thumbnails,
 * lightbox) continue using `PhotoGallery`.
 *
 * @example
 * <FileHandler
 *   accept="image/*,application/pdf"
 *   maxSize={10 * 1024 * 1024}
 *   onFilesSelected={handleFiles}
 *   onReject={(files, reason) => toast.error(`${files.length} file(s) rejected: ${reason}`)}
 *   items={stagedFiles}
 *   renderAction={(item) => <Button onClick={() => remove(item.id)}>Remove</Button>}
 *   emptyActions={<Button variant="secondary">+ Add from InspectPoint</Button>}
 * />
 */
export function FileHandler({ accept, maxSize, disabled = false, uploading = false, onFilesSelected, onReject, items = [], renderAction, renderList, emptyActions, hideEmptyState = false, dropzoneHint, dropzoneNote, dropzoneAriaLabel = 'Select files to upload or drag and drop', listAriaLabel = 'Attached files', uploadingText = 'Uploading…', emptyText = 'No attachments have been added.', className = '' }) {
    const idRef = useRef(nextFileHandlerId());
    const inputId = `qmb-ui-file-handler__input-${idRef.current}`;
    const inputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    // ── Preview state ────────────────────────────────────────────────────────
    const [carouselOpen, setCarouselOpen] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [pdfOpen, setPdfOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    // Register renderAction in the module-level map and build a stable renderer.
    const instanceId = idRef.current;
    actionRendererRegistry.set(instanceId, { fn: renderAction !== null && renderAction !== void 0 ? renderAction : (() => null) });
    const handlePreviewClick = useCallback((item) => {
        var _a;
        const kind = inferKind(item);
        if (kind === 'photo') {
            const photoItems = items.filter((i) => inferKind(i) === 'photo');
            const idx = photoItems.findIndex((i) => i.id === item.id);
            setCarouselIndex(Math.max(0, idx));
            setCarouselOpen(true);
        }
        else if (kind === 'pdf') {
            const url = (_a = item.downloadUrl) !== null && _a !== void 0 ? _a : item.previewUrl;
            if (url) {
                setPdfUrl(url);
                setPdfOpen(true);
            }
        }
        else if (kind === 'other' && item.downloadUrl) {
            const anchor = document.createElement('a');
            anchor.href = item.downloadUrl;
            anchor.download = item.name;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        }
    }, [items]);
    // Register preview handler in the module-level map.
    previewClickRegistry.set(instanceId, { fn: handlePreviewClick });
    useEffect(() => () => {
        actionRendererRegistry.delete(instanceId);
        previewClickRegistry.delete(instanceId);
    }, [instanceId]);
    const stableActionRenderer = useMemo(() => buildActionRenderer(instanceId), [instanceId]);
    const stablePreviewRenderer = useMemo(() => buildPreviewCellRenderer(instanceId), [instanceId]);
    const isInteractive = !disabled && !uploading;
    const processFiles = useCallback((fileList) => {
        if (!fileList || fileList.length === 0)
            return;
        const files = Array.from(fileList);
        const { accepted, sizeRejected, typeRejected } = partitionFiles(files, accept, maxSize);
        if (sizeRejected.length > 0)
            onReject === null || onReject === void 0 ? void 0 : onReject(sizeRejected, 'size');
        if (typeRejected.length > 0)
            onReject === null || onReject === void 0 ? void 0 : onReject(typeRejected, 'type');
        if (accepted.length > 0)
            onFilesSelected(accepted);
    }, [accept, maxSize, onFilesSelected, onReject]);
    const handleInputChange = useCallback((e) => {
        processFiles(e.target.files);
        // Reset so the same file can be re-selected if needed.
        e.target.value = '';
    }, [processFiles]);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (!isInteractive)
            return;
        processFiles(e.dataTransfer.files);
    }, [isInteractive, processFiles]);
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        if (isInteractive)
            setIsDragOver(true);
    }, [isInteractive]);
    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);
    const handleKeyDown = useCallback((e) => {
        var _a;
        if (!isInteractive)
            return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
        }
    }, [isInteractive]);
    // Build action column using the stable module-level renderer.
    const actionColumns = useMemo(() => {
        if (!renderAction)
            return [];
        return [
            {
                dataKey: 'actions',
                label: '',
                columnType: 'action',
                renderer: stableActionRenderer
            }
        ];
    }, [renderAction, stableActionRenderer]);
    const columns = useMemo(() => [
        {
            dataKey: 'preview',
            label: 'Preview',
            renderer: stablePreviewRenderer
        },
        {
            dataKey: 'name',
            label: 'Name',
            columnType: 'primary',
            renderer: NameCellRenderer
        },
        { dataKey: 'typeLabel', label: 'Type' },
        ...actionColumns
    ], [actionColumns, stablePreviewRenderer]);
    // File list section: empty state, custom renderList markup, or the default Table — computed
    // once here so the JSX below stays a single conditional instead of a nested ternary.
    let listSection;
    if (items.length === 0) {
        listSection = hideEmptyState ? null : (React.createElement("div", { className: "qmb-ui-file-handler__list" },
            React.createElement("div", { className: "qmb-ui-file-handler__empty" },
                React.createElement("i", { className: "fa-light fa-paperclip qmb-ui-file-handler__empty-icon", "aria-hidden": "true" }),
                React.createElement(Text, { variant: "body", className: "qmb-ui-file-handler__empty-text" }, emptyText),
                emptyActions && React.createElement("div", { className: "qmb-ui-file-handler__empty-actions" }, emptyActions))));
    }
    else if (renderList) {
        listSection = (React.createElement("div", { className: "qmb-ui-file-handler__list", "aria-label": listAriaLabel }, renderList(items)));
    }
    else {
        listSection = (React.createElement("div", { className: "qmb-ui-file-handler__list", "aria-label": listAriaLabel },
            React.createElement(Table, { variant: "detail", size: TableSize.Full, selectable: false, data: items, columns: columns, 
                // eslint-disable-next-line react/prop-types
                getItemId: (item) => item.id })));
    }
    const rootClasses = ['qmb-ui-file-handler', className].filter(Boolean).join(' ');
    const dropzoneClasses = [
        'qmb-ui-file-handler__dropzone',
        isDragOver ? 'qmb-ui-file-handler__dropzone--drag-over' : '',
        disabled ? 'qmb-ui-file-handler__dropzone--disabled' : '',
        uploading ? 'qmb-ui-file-handler__dropzone--uploading' : ''
    ]
        .filter(Boolean)
        .join(' ');
    return (React.createElement("div", { className: rootClasses },
        React.createElement("div", { className: dropzoneClasses, role: "button", tabIndex: isInteractive ? 0 : -1, "aria-label": dropzoneAriaLabel, "aria-disabled": !isInteractive, onDrop: handleDrop, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onClick: isInteractive ? () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click(); } : undefined, onKeyDown: handleKeyDown },
            React.createElement("input", { ref: inputRef, id: inputId, type: "file", className: "qmb-ui-file-handler__input", accept: accept, multiple: true, disabled: !isInteractive, "aria-hidden": "true", tabIndex: -1, onChange: handleInputChange }),
            uploading ? (React.createElement("div", { className: "qmb-ui-file-handler__dropzone-uploading" },
                React.createElement("i", { className: "fa-light fa-spinner fa-spin qmb-ui-file-handler__dropzone-icon", "aria-hidden": "true" }),
                React.createElement(Text, { variant: "body", className: "qmb-ui-file-handler__dropzone-text" }, uploadingText))) : (React.createElement(React.Fragment, null,
                React.createElement("i", { className: "fa-light fa-cloud-arrow-up qmb-ui-file-handler__dropzone-icon", "aria-hidden": "true" }),
                dropzoneHint ? (React.createElement("div", { className: "qmb-ui-file-handler__dropzone-hint" }, dropzoneHint)) : (
                /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
                React.createElement("label", { htmlFor: inputId, className: "qmb-ui-file-handler__dropzone-label" },
                    React.createElement("span", { className: "qmb-ui-file-handler__dropzone-link" }, "Select the files"),
                    " or drag and drop")),
                dropzoneNote && React.createElement("div", { className: "qmb-ui-file-handler__dropzone-note" }, dropzoneNote)))),
        listSection,
        React.createElement(PhotoCarousel, { open: carouselOpen, index: carouselIndex, slides: items
                .filter((i) => inferKind(i) === 'photo')
                .map((i) => {
                var _a, _b;
                return ({
                    src: (_b = (_a = i.previewUrl) !== null && _a !== void 0 ? _a : i.downloadUrl) !== null && _b !== void 0 ? _b : '',
                    alt: i.name
                });
            }), onClose: () => setCarouselOpen(false), onIndexChange: setCarouselIndex }),
        React.createElement(Modal, { isOpen: pdfOpen, onClose: () => setPdfOpen(false), ariaLabel: "PDF preview", className: PDF_PREVIEW_MODAL_CLASS },
            React.createElement(ModalHeader, { title: "PDF Preview", onClose: () => setPdfOpen(false) }),
            React.createElement(ModalBody, null,
                React.createElement(PDFViewer, { file: pdfUrl })))));
}
